// ═══════════════════════════════════════════════════════════════════════════
// Scenario — framework-agnostic geometry + poses for training
//
// A scenario is pure data: obstacle shapes, rover pose, motion state.
// No 3D engine dependency. LiDAR simulation is ray-geometry intersection.
// IMU simulation is derivatives of motion. Babylon/Three.js/Cesium only
// visualize — they never compute.
//
// The IScenarioProvider interface is extensible via MCP: an LLM can
// generate dynamic scenarios through tool calls, creating obstacle layouts
// that test edge cases the random generator might miss.
// ═══════════════════════════════════════════════════════════════════════════

// ─── Geometry primitives ─────────────────────────────────────────────────────

/**
 * 3D position in meters (world frame, Y-up convention).
 */
export interface IVec3 {
    x: number;
    y: number;
    z: number;
}

/**
 * Axis-aligned bounding box defined by min/max corners.
 */
export interface IAABB {
    min: IVec3;
    max: IVec3;
}

/**
 * Supported obstacle primitive types.
 * Using simple primitives keeps ray intersection math fast and exact.
 */
export const enum ObstacleType {
    /** Axis-aligned box. Defined by center + halfExtents. */
    Box = 0,
    /** Vertical cylinder. Defined by center + radius + height. */
    Cylinder = 1,
    /** Sphere. Defined by center + radius. */
    Sphere = 2,
    /** Infinite ground plane at a given Y height. */
    Plane = 3,
}

/**
 * A single obstacle in the scenario.
 *
 * All shapes are axis-aligned (no rotation) to keep raycasting simple.
 * This is sufficient for training — the MLP needs to learn "something is
 * at distance X in direction Y", not the exact shape of the obstacle.
 */
export interface IObstacle {
    /** Primitive type determines which fields are used. */
    type: ObstacleType;

    /** Center position in world coordinates (meters). */
    center: IVec3;

    /**
     * Half-extents for Box (x, y, z dimensions / 2).
     * Ignored for Cylinder, Sphere, Plane.
     */
    halfExtents?: IVec3;

    /**
     * Radius in meters.
     * Used by Cylinder and Sphere. Ignored for Box and Plane.
     */
    radius?: number;

    /**
     * Height in meters (Cylinder only, extends from center.y - height/2 to center.y + height/2).
     */
    height?: number;

    /**
     * Y height for Plane type. The plane extends infinitely in X and Z.
     */
    planeY?: number;

    /** Optional label for debugging/visualization. */
    label?: string;
}

// ─── Rover state ─────────────────────────────────────────────────────────────

/**
 * Rover pose in the scenario (position + heading).
 * All coordinates in world frame, Y-up.
 */
export interface IPose {
    /** Position in meters. */
    position: IVec3;

    /** Heading in radians (0 = +Z forward, increases clockwise when viewed from above). */
    heading: number;

    /** Pitch in radians (0 = level, positive = nose up). */
    pitch: number;

    /** Roll in radians (0 = level, positive = right side down). */
    roll: number;
}

/**
 * Rover motion state — velocities and accelerations for IMU simulation.
 */
export interface IMotionState {
    /** Linear velocity in m/s (world frame). */
    linearVelocity: IVec3;

    /** Angular velocity in rad/s (body frame: roll, pitch, yaw). */
    angularVelocity: IVec3;

    /** Linear acceleration in m/s² (body frame, includes gravity). */
    linearAcceleration: IVec3;
}

// ─── Scenario ────────────────────────────────────────────────────────────────

/**
 * A complete training scenario: static geometry + rover state.
 *
 * Scenarios are pure data — no methods, no framework dependency.
 * They can be serialized to JSON, stored in datasets, or generated
 * dynamically by an LLM via MCP tool calls.
 */
export interface IScenario {
    /** Unique scenario identifier (for dataset tracking). */
    id: string;

    /** List of obstacles in the environment. */
    obstacles: IObstacle[];

    /** Ground plane Y height (default 0). */
    groundY: number;

    /** Rover pose at the moment of sensor capture. */
    pose: IPose;

    /** Rover motion state at the moment of sensor capture. */
    motion: IMotionState;

    /** Optional goal position for decision training. */
    goal?: IVec3;

    /** Optional tags for filtering/grouping (e.g., "indoor", "narrow-corridor", "rough-terrain"). */
    tags?: string[];

    /**
     * Optional metadata from the scenario source.
     * When generated by an LLM via MCP, this contains the reasoning
     * behind the scenario design (e.g., "testing blind corner detection").
     */
    metadata?: Record<string, unknown>;
}

// ─── LiDAR configuration ────────────────────────────────────────────────────

/**
 * Configuration for simulated LiDAR scanning.
 * Matches the sector layout expected by the PerceptCortex.
 */
export interface ILidarSimConfig {
    /** Number of angular sectors (default 36 = NAVIGATOR_LIDAR_SECTORS). */
    sectorCount: number;

    /** Horizontal field of view in radians (default π = 180°). */
    horizontalFov: number;

    /** Maximum detection range in meters (default 100). */
    maxRange: number;

    /**
     * Number of rays per sector for sub-sampling.
     * More rays = more accurate per-sector min depth but slower.
     * Default: 4.
     */
    raysPerSector: number;
}

// ─── Sensor simulation (overridable) ─────────────────────────────────────────

/**
 * Strategy for simulating sensor readings from a scenario.
 *
 * The training package provides a pure-math CPU implementation
 * (`MathSensorSimulator`) as the default. 3D frameworks override
 * this with GPU-accelerated versions:
 *
 * - **Babylon**: reads the depth buffer via `engine.readPixels()` or
 *   `RenderTargetTexture`, matching the existing MCP CameraLidarFn behavior.
 *   Orders of magnitude faster for complex scenes (meshes, terrain, foliage).
 *
 * - **Three.js**: uses `WebGLRenderer.readRenderTargetPixels()` on a
 *   depth-only render pass.
 *
 * - **Cesium**: uses the globe depth texture for planetary-scale LiDAR.
 *
 * The key insight: real LiDAR simulation IS a depth buffer read.
 * The GPU already solves ray-triangle intersection for every pixel —
 * the depth buffer is literally a dense LiDAR scan.
 *
 * **When to use which:**
 * - CPU math raycaster: offline bulk training data generation (simple scenes,
 *   axis-aligned primitives, millions of samples, no GPU needed)
 * - GPU depth buffer: real-time simulation, complex geometry, validation
 *   against realistic scenes, visual debugging
 */
export interface ISensorSimulator {
    /**
     * Simulate a LiDAR scan from the given pose.
     *
     * @param scenario  Scene geometry and rover state.
     * @param pose      Sensor origin and orientation.
     * @param config    LiDAR parameters (sectors, FOV, range).
     * @returns         Array of `sectorCount` depth values in meters.
     *                  Each value in [0, maxRange]. maxRange = no return.
     */
    simulateLidar(scenario: IScenario, pose: IPose, config: ILidarSimConfig): number[];

    /**
     * Simulate IMU readings from the given motion state.
     *
     * @param motion  Current linear/angular velocity and acceleration.
     * @param noise   Gaussian noise standard deviation (default 0.02).
     * @returns       [ax, ay, az, gx, gy, gz] in body frame.
     */
    simulateIMU(
        motion: IMotionState,
        noise?: number
    ): [number, number, number, number, number, number];
}

// ─── Provider interfaces ─────────────────────────────────────────────────────

/**
 * Generates training scenarios.
 *
 * Implementations:
 * - `RandomScenarioGenerator`: procedural random obstacles (built-in)
 * - MCP-driven generator: an LLM designs scenarios via tool calls
 * - File-based loader: reads scenarios from a JSON dataset
 *
 * The MCP extensibility point allows an LLM to:
 * 1. Analyze current training performance (which scenarios cause failures)
 * 2. Design targeted scenarios that stress-test weak areas
 * 3. Generate edge cases a random generator would rarely produce
 *    (e.g., "narrow L-shaped corridor with a dead end")
 */
export interface IScenarioProvider {
    /**
     * Generate a single scenario.
     * @returns A complete scenario ready for sensor simulation.
     */
    generate(): IScenario;

    /**
     * Generate a batch of scenarios.
     * @param count  Number of scenarios to generate.
     * @returns      Array of independent scenarios.
     */
    generateBatch(count: number): IScenario[];
}

/**
 * MCP-extensible scenario provider.
 *
 * Extends IScenarioProvider with methods that an MCP behavior can call
 * to inject LLM-designed scenarios into the training pipeline.
 *
 * The MCP tool schema would expose:
 * - `addScenario(scenario: IScenario)` — inject a hand-crafted scenario
 * - `setConstraints(constraints)` — guide the random generator
 * - `getPerformanceReport()` — read which scenarios cause MLP failures
 */
export interface IMcpScenarioProvider extends IScenarioProvider {
    /**
     * Inject a scenario designed by an external source (e.g., LLM via MCP).
     * The scenario is added to the generation queue and will be included
     * in the next `generate()` or `generateBatch()` call.
     */
    addScenario(scenario: IScenario): void;

    /**
     * Set constraints for the random generator.
     * An LLM can use this to steer generation toward specific difficulty
     * ranges or obstacle configurations.
     */
    setConstraints(constraints: IScenarioConstraints): void;

    /**
     * Get a summary of which scenario types cause the most MLP failures.
     * An LLM can use this to design targeted counter-examples.
     */
    getPerformanceReport(): IScenarioPerformanceReport;
}

/**
 * Constraints that guide procedural scenario generation.
 * All fields are optional — omitted fields use defaults.
 */
export interface IScenarioConstraints {
    /** Minimum number of obstacles per scenario. Default: 1. */
    minObstacles?: number;

    /** Maximum number of obstacles per scenario. Default: 20. */
    maxObstacles?: number;

    /** Minimum distance from rover to nearest obstacle (meters). Default: 0.5. */
    minObstacleDistance?: number;

    /** Maximum distance from rover to nearest obstacle (meters). Default: 50. */
    maxObstacleDistance?: number;

    /** Restrict to specific obstacle types. Default: all types. */
    allowedTypes?: ObstacleType[];

    /** Force scenarios with obstacles in specific angular sectors. */
    forcedSectors?: number[];

    /** Minimum terrain roughness (IMU vibration). Default: 0. */
    minRoughness?: number;

    /** Maximum terrain roughness. Default: 1. */
    maxRoughness?: number;

    /** Tags to apply to all generated scenarios. */
    tags?: string[];
}

/**
 * Performance report for MCP-driven adaptive training.
 */
export interface IScenarioPerformanceReport {
    /** Total scenarios evaluated. */
    totalEvaluated: number;

    /** Scenarios where the MLP produced risk > threshold. */
    highRiskCount: number;

    /** Scenarios where the MLP output diverged significantly from ground truth. */
    highErrorCount: number;

    /** Most common tags among high-error scenarios. */
    weaknessTags: string[];

    /** Average MSE per perception output index. */
    perFeatureMSE: number[];
}
